import React, {useMemo, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Accordion} from '@/components/molecules/Accordion';
import {AccordionItem} from '@/components/molecules/AccordionItem';
import {Link, Tab, TabsNavigation, Icon} from '@/components/atoms';
import {FileUploadWithModal, ReviewProductsTable} from '@/components/organisms';
import {useInfiniteScrollTable} from '@/hooks/useInfiniteScrollTable';
import {getFormattedDate} from '@/utils/commons';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate} from 'react-router-dom';
import './reviewAccordion.scss';
import {useAppSelector, useAppDispatch} from '@/store';
import {
  API_STATUS,
  Constants,
  FILE_UPLOAD_TEMPLATE_TYPE
} from '@/utils/constants/commonConstants';
import {ReviewDisplayContentTab, ReviewParentChildTab} from '@/containers';
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants';
import {isCdaSuperTargetType} from '@/utils/salesTargetStepConfig';
import {isProductGroupQuantityType} from '@/utils/productGroupQuantity';
import {ProductGtinAssignmentTable} from '@/components/molecules/ProductGtinAssignmentTable';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {ButtonIcon} from '@pepsico-ds/ui';
import {ReviewRow} from '@/containers/ReviewRow';
import ImageViewer from '@/components/atoms/ImageViewer';
import getTargetRules from '@/helpers/targetRulesHelper';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {getSasToken} from '@/services/sasToken';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {setSalesTarget} from '@/store/slices/salesTargetSlice';
import {logger} from '@/helpers/logger';

function EditSectionButton({
  route,
  testId,
  className = '',
  status,
  canEditPublishedBeforeStart,
  onEdit,
  editSectionLink
}) {
  const isEditableStatus =
    status == null ||
    (status && status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase()) ||
    Boolean(canEditPublishedBeforeStart) ||
    (status && status?.toLowerCase() === Constants.InProgress.toLowerCase());
  if (!isEditableStatus) {
    return null;
  }

  return (
    <button
      type='button'
      data-testid={testId}
      onClick={() => onEdit(route)}
      className={`edit-button ${className}`.trim()}>
      {editSectionLink}
    </button>
  );
}

EditSectionButton.propTypes = {
  route: PropTypes.string.isRequired,
  testId: PropTypes.string,
  className: PropTypes.string,
  status: PropTypes.string,
  canEditPublishedBeforeStart: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  editSectionLink: PropTypes.node.isRequired
};

function ParentChildTabsNavigation({
  testId,
  className,
  activeTabValue,
  section,
  onTabChange
}) {
  return (
    <TabsNavigation
      data-testid={testId}
      className={className}
      activeTabIndex={activeTabValue === 'parent' ? 0 : 1}>
      {['parent', 'child'].map((tab, index) => (
        <Tab
          key={tab}
          data-testid={`tab-${tab}`}
          label={index === 0 ? 'Parent' : 'Child'}
          className='no-icon'
          onClick={() => onTabChange(tab, section)}
          isDark
        />
      ))}
    </TabsNavigation>
  );
}

ParentChildTabsNavigation.propTypes = {
  testId: PropTypes.string,
  className: PropTypes.string,
  activeTabValue: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired
};

function ReviewGridRow({title, value}) {
  return (
    <div className='review_grid_row grid grid-cols-2 gap-y-2'>
      <div className='review_title'>
        <span>{title}</span>
      </div>
      <div className='review_value text-white'>
        <span>{value}</span>
      </div>
    </div>
  );
}

ReviewGridRow.propTypes = {
  title: PropTypes.node,
  value: PropTypes.node
};

function ReviewAccordion({
  salesTargetData,
  languages,
  handleTabClick,
  activeLanguage,
  activeTab = 'parent',
  editPanel,
  status,
  canEditPublishedBeforeStart,
  preRequisiteParentTab,
  preRequisiteChildTab,
  handleSetActiveTab,
  getFilteredProducts,
  onTemplateUpdate = () => {},
  salesTargetId
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {downloadFileOnBlob} = useAzureBlob();
  //const { formData } = salesTargetData
  const {isAllCategories, isAllCategoriesChild, selectedType} = useAppSelector(
    (state) => state.salesTarget
  );
  const parentSelectedCategory = useAppSelector(
    (state) => state.salesTarget.tabWiseData.parent.selectedCategory
  );
  const childSelectedCategory = useAppSelector(
    (state) => state.salesTarget.tabWiseData.child.selectedCategory
  );
  const [openUpload, setOpenUpload] = useState(false);
  const [formData, setFormData] = useState(salesTargetData.formData);
  const countryCode = useAppSelector(selectCountryCode);

  useEffect(() => {
    // Keep local formData in sync with salesTargetData changes
    setFormData(salesTargetData.formData);
  }, [salesTargetData.formData]);

  const filteredProducts = useMemo(
    () => (getFilteredProducts ? getFilteredProducts() : []),
    [getFilteredProducts]
  );
  const tableData = useInfiniteScrollTable(filteredProducts);

  const getAnswer = (val) => (val ? 'Yes' : 'No');
  const [selectedTab, setSelectedTab] = useState(activeTab);
  const [selectedTabForProduct, setSelectedTabForProduct] = useState(activeTab);

  useEffect(() => {
    setSelectedTab(activeTab);
    setSelectedTabForProduct(activeTab);
  }, [activeTab]);

  const handleTabChange = (tab, section) => {
    if (section === 'display-content') {
      setSelectedTab(tab);
    } else {
      setSelectedTabForProduct(tab);
    }
  };

  const handleEditSection = (route) => {
    const isDraftStatus =
      salesTargetData.status &&
      salesTargetData.status?.toLowerCase() === API_STATUS.DRAFT?.toLowerCase();

    if (isDraftStatus) {
      editPanel(route);
      return;
    }

    const isInProgressStatus =
      salesTargetData.status &&
      salesTargetData.status?.toLowerCase() ===
        Constants.InProgress.toLowerCase();

    handleSetActiveTab();
    dispatch(setSalesTarget(salesTargetData));

    // For In-Progress targets, pass salesTargetId similar to Published targets
    const navigationState = {
      isEdit: true,
      editPublishedBeforeStart: Boolean(canEditPublishedBeforeStart),
      disableDraft: Boolean(canEditPublishedBeforeStart) || isInProgressStatus,
      salesTargetId: salesTargetId || salesTargetData?.id
    };

    navigate(route, {state: navigationState});
  };

  const editSectionLink = (
    <span className='link $color-link flex items-center gap-1'>
      <Icon style={{color: '#3387df'}} icon='edit' size='small' />
      Edit Section
    </span>
  );

  const fileName = formData?.storeList?.split('/')?.pop();

  const getSelectedCategoryTitle = () => {
    let newSelectedCategory = parentSelectedCategory;
    let isAllCategoriesNew = isAllCategories;

    if (isPrerequisiteTarget && selectedTabForProduct === 'child') {
      newSelectedCategory = childSelectedCategory;
      isAllCategoriesNew = isAllCategoriesChild;
    }

    // For prerequisite targets, derive selected type per tab (parent/child) instead of relying on global selectedType
    let selectedTypeForTab = selectedType;
    if (isPrerequisiteTarget) {
      const isParentTab = selectedTabForProduct === 'parent';
      const tabConfig = isParentTab
        ? preRequisiteParentTab
        : preRequisiteChildTab;
      selectedTypeForTab = tabConfig?.uploadFile
        ? Constants.UploadedProducts
        : Constants.CategoryProducts;
    }

    if (
      isAllCategoriesNew &&
      selectedTypeForTab !== Constants.UploadedProducts
    ) {
      return 'All Categories';
    } else if (
      newSelectedCategory?.length > 0 &&
      selectedTypeForTab !== Constants.UploadedProducts
    ) {
      if (typeof newSelectedCategory[0] == 'object') {
        return newSelectedCategory?.map((item) => item?.displayText).join(', ');
      } else {
        return newSelectedCategory?.map((item) => item).join(', ');
      }
    } else {
      return 'No Category Selected';
    }
  };

  const isPrerequisiteTarget =
    salesTargetData?.targetType?.toLowerCase() ==
      TARGET_TYPES.MonthlyTargetPrerequistite.toLowerCase() ||
    salesTargetData?.targetType?.toLowerCase() ==
      TARGET_TYPES.ProductTargetPrerequisite.toLowerCase();

  const isLayeredTarget =
    salesTargetData?.targetType === TARGET_TYPES.MonthlyTargetLayered ||
    salesTargetData?.targetType === TARGET_TYPES.ProductTargetLayered;

  const isEarlyPaymentTarget =
    salesTargetData?.targetType === TARGET_TYPES.EarlyPaymentTarget;

  const isDropSizeTarget =
    salesTargetData?.targetType === TARGET_TYPES.DropSizeTarget;

  const isLinkedTargetType = isEarlyPaymentTarget || isDropSizeTarget;

  const isRackChallenge =
    salesTargetData?.targetType === TARGET_TYPES.RackChallenge;

  // isLinkedTargetType (AMESA) and isRackChallenge (LATAM) are mutually exclusive, both skip Products
  const isCdaSuperTarget = isCdaSuperTargetType(salesTargetData?.targetType);

  const isThreeStepTarget =
    isLinkedTargetType || isRackChallenge || isCdaSuperTarget;

  const isProductGroupQuantity = isProductGroupQuantityType(
    salesTargetData?.targetType
  );

  const downloadCSVforTargetSetup = async () => {
    try {
      const fileUrl = salesTargetData.formData.storeList;

      // Extract fileName and directory from the URL
      const blobFileName = fileUrl?.split('/').pop();
      const directory = fileUrl?.split('/input')[1];

      const sasResponse = await getSasToken(
        blobFileName,
        FILE_UPLOAD_TEMPLATE_TYPE.SALES_TARGET
      );
      const response = await downloadFileOnBlob(
        sasResponse,
        directory,
        blobFileName
      );
      if (!response && !response?.responseText)
        throw new Error('No response received from download');

      const responseText = response?.responseText;

      const fileNameTemp = `Target_Template${getDateWithTimeString()}`;
      downloadCSVFiles(responseText, fileNameTemp);
    } catch (error) {
      logger.error('Error downloading CSV:', error);
    }
  };

  let rule;
  let mediaFile;

  if (selectedTab === 'parent') {
    rule =
      salesTargetData?.tabWiseData?.parent?.ruleSet?.rules?.[0] ||
      salesTargetData?.ruleSet?.rules?.[0];
    mediaFile = salesTargetData?.tabWiseData?.parent?.mediaFile?.[0];
  } else if (selectedTab === 'child') {
    rule = salesTargetData?.tabWiseData?.child?.ruleSet?.rules?.[0];
    mediaFile = salesTargetData?.tabWiseData?.child?.mediaFile?.[0];
  } else {
    rule = salesTargetData?.ruleSet?.rules?.[0];
    mediaFile = salesTargetData?.mediaFile?.[0];
  }

  const includedProductIds = useMemo(
    () => rule?.includedProductIds || [],
    [rule]
  );

  const onDownloadClick = () => {
    if (!includedProductIds.length) return;
    const csvHeader = 'Product Id\n';
    const csvRows = includedProductIds.join('\n');
    const csvString = csvHeader + csvRows;
    downloadCSVFiles(csvString, mediaFile);
  };

  const SelectionProductList = () => {
    return (
      <span>
        {salesTargetData?.tabWiseData?.parent?.includedProducts?.length > 0 ||
        salesTargetData?.tabWiseData?.parent?.excludedProducts?.length > 0
          ? 'CURRENT LIST'
          : '-'}
      </span>
    );
  };

  const isTemplateUploaded = (fileName) => {
    return fileName ? 'Yes - ' : 'No';
  };

  const isInProgressOrPublished = useMemo(() => {
    return (
      status?.toLowerCase() === Constants.InProgress?.toLowerCase() ||
      status?.toLowerCase() === Constants.Published?.toLowerCase()
    );
  }, [status]);

  const isPublished =
    status?.toLowerCase() === Constants.Published?.toLowerCase() ||
    status?.toLowerCase() === API_STATUS.PUBLISHED?.toLowerCase();
  const isInProgress =
    status?.toLowerCase() === Constants.InProgress?.toLowerCase();

  const canEditPublished = Boolean(canEditPublishedBeforeStart);

  const isEditTemplateVisible =
    !isLinkedTargetType &&
    (isInProgress ||
      (isPublished && canEditPublished) ||
      (!isPublished && isInProgressOrPublished));

  const isDisplayContentEditable = isInProgress;

  const handleEditDisplayContent = () => {
    handleSetActiveTab();
    dispatch(setSalesTarget(salesTargetData));
    navigate(
      isRackChallenge
        ? ROUTES.RACK_CHALLENGE_DISPLAY_CONTENT
        : ROUTES.SALES_TARGET_DISPLAY_CONTENT,
      {
        state: {
          isEdit: true,
          editMode: 'displayContent',
          returnTo: `${ROUTES.SALES_TARGET_DETAIL.replace(
            ':id',
            salesTargetId || salesTargetData?.id
          )}?status=${status}`,
          editPublishedBeforeStart: Boolean(canEditPublishedBeforeStart),
          disableDraft: Boolean(canEditPublishedBeforeStart),
          salesTargetId: salesTargetId || salesTargetData?.id
        }
      }
    );
  };

  const renderSetupEditButton = () => (
    <EditSectionButton
      route={ROUTES.SALES_TARGET_ADD}
      testId='targetSetupEdit'
      status={status}
      canEditPublishedBeforeStart={canEditPublishedBeforeStart}
      onEdit={handleEditSection}
      editSectionLink={editSectionLink}
    />
  );

  const renderSetupAccordionContent = () => (
    <>
      <div className='review_subtitle mt-4'>Target Setup</div>
      <div className='mb-4 border-b border-[color:var(--color-border-divider)]' />
      <div className='grid grid-cols-2 gap-8'>
        <div className='grid grid-cols-1 '>
          <div className='grid_wrap'>
            <ReviewGridRow
              title='Target Type'
              value={
                formData?.targetType &&
                formData?.targetType !== 'INVALID' &&
                formData?.targetType !== 'Invalid'
                  ? formData?.targetType
                  : '-'
              }
            />
            <ReviewGridRow title='Target Name' value={formData?.targetName} />
            {isRackChallenge && (
              <ReviewGridRow
                title='Challenge Transaction ID'
                value={formData?.challengeTransactionId || '-'}
              />
            )}
            {isLinkedTargetType && (
              <ReviewGridRow
                title='Linked Sales Target'
                value={
                  salesTargetData?.linkedSalesTargets?.length > 0
                    ? salesTargetData.linkedSalesTargets
                        .map((lt) => lt.name)
                        .join(', ')
                    : '-'
                }
              />
            )}
            {isCdaSuperTarget && (
              <ReviewGridRow title='Sales Target Conditions' value='Pending' />
            )}
            {!isRackChallenge && (
              <div className='review_grid_row grid grid-cols-2 gap-y-2'>
                <div className='review_title'>
                  <span>Target Template</span>
                </div>
                <div className='review_value text-white'>
                  <span>{isTemplateUploaded(fileName)}</span>
                  <Link
                    className='text-sky-500'
                    onClick={() => downloadCSVforTargetSetup()}>
                    <span>{fileName}</span>
                  </Link>
                  {isEditTemplateVisible && (
                    <button
                      className='mt-2 text-blue-500 underline'
                      type='button'
                      onClick={() => setOpenUpload(true)}
                      style={{
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: fileName ? 0 : '4px 16px 4px 16px'
                      }}>
                      Edit Template
                    </button>
                  )}
                  <FileUploadWithModal
                    openUpload={openUpload}
                    setOpenUpload={setOpenUpload}
                    setFormData={setFormData}
                    formData={{
                      ...formData,
                      targetMediaFile: [formData?.storeList]
                    }}
                    uploadFor={Constants.SalesTarget}
                    fileType='csv'
                    isCsvValidationRequired={!isCdaSuperTarget}
                    onTemplateUpdate={onTemplateUpdate}
                    salesTargetId={salesTargetId}
                    customRules={getTargetRules({
                      targetType: formData?.targetType,
                      numberLevel: formData?.numberLevel,
                      numberMileStone: formData?.numberMileStone,
                      countryCode: countryCode,
                      productGtins: salesTargetData?.productGtins
                    })}
                  />
                </div>
              </div>
            )}

            {/* <div className="review_value text-white">
                            <span>
                              NO{' '}
                              <a className="text-sky-500" onClick={() => setOpenStoreListModal(true)}>
              STORE-LIST-OCT2024.CSV
            </a>
            <ReviewStoreListModal
              isOpenStoreListModal={isOpenStoreListModal}
              setOpenStoreListModal={setOpenStoreListModal}
            />
                            </span>
                          </div> */}
            {/* <div className="review_grid_row grid grid-cols-2 gap-y-2">
                          <div className="review_title">
                            <span>Default Spend Goal</span>
                          </div>
                          <div className="review_value text-white">
                            <span>{formData?.defaultSpendGoal}</span>
                          </div>
                        </div> */}
            {/* <div className="review_grid_row grid grid-cols-2 gap-y-2">
                          <div className="review_title">
                            <span>Points to Award</span>
                          </div>
                          <div className="review_value text-white">
                            <span>{formData?.defaultPointsAwarded}</span>
                          </div>
                        </div> */}
            {isLayeredTarget && (
              <ReviewGridRow
                title='Point Display'
                value={formData?.pointDisplay || '-'}
              />
            )}
            {!isLayeredTarget &&
              !isLinkedTargetType &&
              !isRackChallenge &&
              !isCdaSuperTarget && (
                <ReviewGridRow
                  title='Exclude tax from goal?'
                  value={getAnswer(formData?.excludeTaxFromGoal)}
                />
              )}
          </div>
        </div>
        <div className='grid grid-cols-1 '>
          <div className='grid_wrap'>
            {!isRackChallenge && !isCdaSuperTarget && (
              <ReviewGridRow
                title='Include Offline Orders?'
                value={getAnswer(formData?.offlineOrdersIncluded)}
              />
            )}
            {!isLinkedTargetType && !isRackChallenge && !isCdaSuperTarget && (
              <ReviewGridRow
                title='Round up points?'
                value={getAnswer(formData?.roundUpPointsFlag)}
              />
            )}
            {!isLinkedTargetType && !isRackChallenge && !isCdaSuperTarget && (
              <ReviewGridRow
                title='Users need to join this campaign?'
                value={getAnswer(formData?.joinCampaignFlag)}
              />
            )}
            {isLayeredTarget && (
              <ReviewGridRow
                title='Exclude tax from goal?'
                value={getAnswer(formData?.excludeTaxFromGoal)}
              />
            )}
          </div>
        </div>
      </div>

      <div className='review_subtitle'>Availability </div>
      <div className='grid grid-cols-2 gap-8'>
        <div className='grid grid-cols-1 '>
          <div className='grid_wrap'>
            <ReviewGridRow
              title='Active Start Date'
              value={getFormattedDate(
                formData?.startDate,
                false,
                '-',
                'yyyy-mm-dd'
              )}
            />
            <ReviewGridRow
              title='Active End Date'
              value={getFormattedDate(
                formData?.endDate,
                false,
                '-',
                'yyyy-mm-dd'
              )}
            />
            <ReviewGridRow
              title='Display Start Date'
              value={getFormattedDate(
                formData?.displayStartDate,
                false,
                '-',
                'yyyy-mm-dd'
              )}
            />
            <ReviewGridRow
              title='Active and Display the same?'
              value={getAnswer(formData?.activeDisplay)}
            />
          </div>
        </div>

        <div className='grid grid-cols-1 '>
          <div className='grid_wrap'>
            {isLinkedTargetType ? (
              <ReviewGridRow
                title='Points Delivery Date'
                value={getFormattedDate(
                  salesTargetData?.pointsDeliveryDate,
                  false,
                  '-',
                  'yyyy-mm-dd'
                )}
              />
            ) : (
              <>
                <ReviewGridRow
                  title='Target Delivery Date'
                  value={getFormattedDate(
                    formData?.targetDeliveryDate,
                    false,
                    '-',
                    'yyyy-mm-dd'
                  )}
                />
                {!isCdaSuperTarget && (
                  <ReviewGridRow
                    title='Awards points immediately when goal is hit?'
                    value={getAnswer(formData?.awardPointsImmedFlag)}
                  />
                )}
                <ReviewGridRow
                  title='Points Delivery Date'
                  value={getFormattedDate(
                    formData?.pointsDeliveryDate,
                    false,
                    '-',
                    'yyyy-mm-dd'
                  )}
                />
              </>
            )}
            <ReviewGridRow
              title='Display End Date'
              value={getFormattedDate(
                formData?.displayEndDate,
                false,
                '-',
                'yyyy-mm-dd'
              )}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderRackChallengeDisplayContent = () => {
    const media = salesTargetData?.displayContentMedia;
    const imageUri = media?.imageUri || media?.imageUrl;
    const visibleLanguages = languages || [];

    return (
      <>
        <div className='review_subtitle mt-4'>Display Image</div>
        <div className='mb-4 border-b border-[color:var(--color-border-divider)]' />
        <div className='mb-6'>
          <div className='review_title mb-2 text-sm text-[color:var(--color-text-secondary)]'>
            <span>Target Image</span>
          </div>
          {imageUri && <ImageViewer url={imageUri} alt='Target Image' />}
        </div>

        <div className='review_subtitle mb-4'>Display Information</div>
        <div
          className='grid gap-8'
          style={{
            gridTemplateColumns: `repeat(${Math.min(visibleLanguages.length, 3)}, minmax(0, 1fr))`
          }}>
          {visibleLanguages.map((lang) => {
            const data = salesTargetData?.displayContent?.[lang.code] || {};
            return (
              <div key={lang.code} className='min-w-0'>
                <div className='review_subtitle mb-4 border-b border-[color:var(--color-border-divider)] pb-2'>
                  {lang.title}
                </div>
                <div className='mb-4'>
                  <div className='review_title text-sm text-[color:var(--color-text-secondary)]'>
                    <span>Title</span>
                  </div>
                  <div className='review_value break-words text-white'>
                    <span>{data.title}</span>
                  </div>
                </div>
                <div className='mb-4'>
                  <div className='review_title text-sm text-[color:var(--color-text-secondary)]'>
                    <span>Description</span>
                  </div>
                  <div
                    className='review_value rich-text-content break-words text-white'
                    dangerouslySetInnerHTML={{__html: data.description || ''}}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderDisplayContentEditButton = () => {
    const route = isRackChallenge
      ? ROUTES.RACK_CHALLENGE_DISPLAY_CONTENT
      : ROUTES.SALES_TARGET_DISPLAY_CONTENT;
    if (isDisplayContentEditable) {
      return (
        <button
          type='button'
          data-testid='displayContentEdit'
          onClick={handleEditDisplayContent}
          className='edit-button'>
          {editSectionLink}
        </button>
      );
    }
    return (
      <EditSectionButton
        route={route}
        testId='displayContentEdit'
        status={status}
        canEditPublishedBeforeStart={canEditPublishedBeforeStart}
        onEdit={handleEditSection}
        editSectionLink={editSectionLink}
      />
    );
  };

  const renderDisplayContentAccordionContent = () => {
    if (isRackChallenge) {
      return renderRackChallengeDisplayContent();
    }
    return (
      <>
        {isPrerequisiteTarget ? (
          <>
            <ParentChildTabsNavigation
              testId='review-display-content-tabs'
              className='w-70 display-content-nav w-full border-[color:var(--color-border-divider)]'
              activeTabValue={selectedTab}
              section='display-content'
              onTabChange={handleTabChange}
            />
            <ReviewParentChildTab
              selectedTab={selectedTab}
              languages={languages}
              activeLanguage={activeLanguage}
              salesTargetData={salesTargetData}
              section='display-content'
            />
          </>
        ) : (
          <>
            <TabsNavigation
              activeTabIndex={0}
              handleSetActiveTab={function Qa() {}}
              className='mb-5'>
              {languages &&
                languages?.length > 0 &&
                languages.map((lang, idx) => (
                  <Tab
                    key={`${idx}_${lang.code}`}
                    label={lang.title}
                    className='no-icon'
                    onClick={() => handleTabClick(lang.code)}
                    isDark
                  />
                ))}
            </TabsNavigation>
            <ReviewDisplayContentTab
              languages={languages}
              activeLanguage={activeLanguage}
              salesTargetData={salesTargetData}
              isLinkedTargetType={isLinkedTargetType}
              isRackChallenge={isRackChallenge}
              isProductGroupQuantity={isProductGroupQuantity}
            />
          </>
        )}
      </>
    );
  };

  const renderProductsEditButton = () => (
    <EditSectionButton
      route={ROUTES.SALES_TARGET_PRODUCTS}
      testId='productSelectionEdit'
      status={status}
      canEditPublishedBeforeStart={canEditPublishedBeforeStart}
      onEdit={handleEditSection}
      editSectionLink={editSectionLink}
    />
  );

  const renderProductsAccordionContent = () => (
    <>
      {isProductGroupQuantity ? (
        <div className='review_subtitle' />
      ) : (
        <div className='review_subtitle'>Target Setup </div>
      )}

      {isProductGroupQuantity ? (
        <ProductGtinAssignmentTable
          productGtins={salesTargetData?.productGtins}
        />
      ) : isPrerequisiteTarget ? (
        <>
          <ParentChildTabsNavigation
            testId='review-product-tabs'
            className='w-70 display-content-nav mb-6 w-full border-[color:var(--color-border-divider)]'
            activeTabValue={selectedTabForProduct}
            section='product'
            onTabChange={handleTabChange}
          />
          <ReviewParentChildTab
            selectedTab={selectedTabForProduct}
            languages={languages}
            activeLanguage={activeLanguage}
            salesTargetData={salesTargetData}
            getSelectedCategoryTitle={getSelectedCategoryTitle}
            section='product'
            preRequisiteParentTabData={preRequisiteParentTab}
            preRequisiteChildTabData={preRequisiteChildTab}
            getFilteredProducts={getFilteredProducts}
          />
        </>
      ) : (
        <>
          {selectedType === Constants.CategoryProducts ? (
            <>
              <div className='grid grid-cols-2 gap-8'>
                <div className='grid grid-cols-1 '>
                  <div className='grid_wrap'>
                    <ReviewGridRow
                      title='Ignore SKU List'
                      value={getAnswer(
                        salesTargetData?.tabWiseData?.parent?.ignoreSkuList
                      )}
                    />
                    <div className='review_grid_row grid grid-cols-2 gap-y-2'>
                      <div className='review_title'>
                        <span>Product Selection</span>
                      </div>
                      <div className='review_value text-white'>
                        <SelectionProductList />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='grid grid-cols-1 '>
                  <div className='grid_wrap'>
                    <ReviewGridRow
                      title='Category'
                      value={getSelectedCategoryTitle()}
                    />
                  </div>
                </div>
              </div>
              <ReviewProductsTable
                displayedProducts={tableData.displayedProducts}
                isLoading={tableData.isLoading}
                hasMoreData={tableData.hasMoreData}
                totalProducts={tableData.totalProducts}
                onScroll={tableData.handleScroll}
                containerRef={tableData.tableContainerRef}
                itemsPerPage={tableData.itemsPerPage}
              />
            </>
          ) : (
            <div className='grid'>
              <div className='grid_wrap flex-direction-row flex'>
                <ReviewRow
                  label='Product List'
                  value={salesTargetData?.tabWiseData?.parent?.mediaFile[0]}
                />
                <ButtonIcon
                  icon='file_download'
                  isDark
                  size='medium'
                  type='default'
                  data-testid='file_download'
                  onClick={onDownloadClick}
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div className='sales-target-review'>
      <Accordion
        isDark
        size='large'
        content={
          <>
            <AccordionItem
              isDark
              expanded
              label={<span className='font-bold'>Setup</span>}
              content={renderSetupAccordionContent()}
              rightAccessoryChild={renderSetupEditButton()}
            />
            <AccordionItem
              isDark
              expanded
              label={<span className='font-bold'>Display Content</span>}
              content={renderDisplayContentAccordionContent()}
              rightAccessoryChild={renderDisplayContentEditButton()}
            />
            {!isThreeStepTarget && (
              <AccordionItem
                isDark
                expanded
                label={
                  <span className='font-bold'>
                    {isProductGroupQuantity ? 'Product Assignment' : 'Products'}
                  </span>
                }
                content={renderProductsAccordionContent()}
                rightAccessoryChild={renderProductsEditButton()}
              />
            )}
          </>
        }
      />
    </div>
  );
}
export default React.memo(ReviewAccordion);

ReviewAccordion.propTypes = {
  salesTargetData: PropTypes.object.isRequired,
  languages: PropTypes.array,
  handleTabClick: PropTypes.func.isRequired,
  activeLanguage: PropTypes.string.isRequired,
  activeTab: PropTypes.string,
  editPanel: PropTypes.func.isRequired,
  status: PropTypes.string,
  canEditPublishedBeforeStart: PropTypes.bool,
  preRequisiteParentTab: PropTypes.object,
  preRequisiteChildTab: PropTypes.object,
  handleSetActiveTab: PropTypes.func,
  getFilteredProducts: PropTypes.func,
  onTemplateUpdate: PropTypes.func,
  salesTargetId: PropTypes.string
};
